/**
 * CourseyVoyage GA 服務
 * 封裝 Firebase Analytics (GA4)，在 SSR 或未初始化時安全 no-op。
 */

import { logEvent, setUserId as setFirebaseUserId } from 'firebase/analytics';
import { analytics } from '../firebase/config';

const isAvailable = (): boolean => typeof analytics !== 'undefined' && analytics !== null;

/** 記錄頁面瀏覽 (GA4 page_view) */
export function trackPageView(path: string, pageTitle?: string): void {
    if (!isAvailable() || !analytics) return;
    try {
        logEvent(analytics, 'page_view', {
            page_location: typeof window !== 'undefined' ? window.location.origin + path : path,
            page_title: pageTitle || path || 'CourseyVoyage',
        });
    } catch (e) {
        if (process.env.NODE_ENV === 'development') {
            console.warn('[analytics] page_view failed', e);
        }
    }
}

/** 記錄自訂事件 */
export function trackEvent(eventName: string, params?: Record<string, string | number | boolean>): void {
    if (!isAvailable() || !analytics) return;
    try {
        logEvent(analytics, eventName, params as Record<string, any>);
    } catch (e) {
        if (process.env.NODE_ENV === 'development') {
            console.warn('[analytics] event failed', eventName, e);
        }
    }
}

/** 設定 GA 使用者 ID（登入後呼叫） */
export function setUserId(uid: string | null): void {
    if (!isAvailable() || !analytics) return;
    try {
        setFirebaseUserId(analytics, uid ?? '');
    } catch (e) {
        if (process.env.NODE_ENV === 'development') {
            console.warn('[analytics] setUserId failed', e);
        }
    }
}

/** 登入成功：method = guest | google | upgrade_google */
export function trackLogin(method: 'guest' | 'google' | 'upgrade_google'): void {
    trackEvent('login', { method });
}

/** 登出 */
export function trackLogout(): void {
    trackEvent('logout');
}

/** 新增課程 */
export function trackCourseAdd(childId: string, courseType: string): void {
    trackEvent('course_add', { child_id: childId, course_type: courseType });
}

/** 編輯課程（儲存） */
export function trackCourseEdit(childId: string, courseId: string): void {
    trackEvent('course_edit', { child_id: childId, course_id: courseId });
}

/** 刪除課程 */
export function trackCourseDelete(childId: string, courseId: string): void {
    trackEvent('course_delete', { child_id: childId, course_id: courseId });
}

/** 新增/編輯繳費記錄 */
export function trackPaymentUpdate(childId: string, courseId: string, action: 'add' | 'edit'): void {
    trackEvent('payment_update', { child_id: childId, course_id: courseId, action });
}

/** 新增/編輯退費記錄 */
export function trackRefundUpdate(childId: string, courseId: string, action: 'add' | 'edit'): void {
    trackEvent('refund_update', { child_id: childId, course_id: courseId, action });
}

/** 課程紀錄（上課/請假/補課/取消） */
export function trackLessonRecord(childId: string, courseId: string, recordType: string): void {
    trackEvent('lesson_record', { child_id: childId, course_id: courseId, record_type: recordType });
}

/** 暫停/恢復課程 */
export function trackCoursePause(childId: string, courseId: string, paused: boolean): void {
    trackEvent('course_pause', { child_id: childId, course_id: courseId, paused: paused ? 'true' : 'false' });
}

/** 新增兒童 */
export function trackChildAdd(): void {
    trackEvent('child_add');
}

/** 編輯兒童 */
export function trackChildEdit(): void {
    trackEvent('child_edit');
}

/** 刪除兒童 */
export function trackChildDelete(): void {
    trackEvent('child_delete');
}

/** PWA 安裝：接受 */
export function trackInstallAccept(): void {
    trackEvent('pwa_install_accept');
}

/** PWA 安裝：關閉/不再顯示 */
export function trackInstallDismiss(): void {
    trackEvent('pwa_install_dismiss');
}

/** 設定變更：通知、提醒閾值等 */
export function trackSettingsChange(settingKey: string, value?: string): void {
    const params: Record<string, string> = { setting_key: settingKey };
    if (value !== undefined) params.setting_value = value;
    trackEvent('settings_change', params);
}

/** 版本更新彈窗：使用者點擊「現在更新」 */
export function trackVersionUpdateAccept(): void {
    trackEvent('version_update_accept');
}

/** 版本更新彈窗：使用者點擊「稍後提醒」或關閉 */
export function trackVersionUpdateDismiss(): void {
    trackEvent('version_update_dismiss');
}
